import {
  Body,
  Box,
  Space,
  LocalizedString,
  ErrorBanner,
  Loader,
} from "@dbn/omnichannel-design-system-g4p";
import { array, option, task, taskEither } from "fp-ts";
import { TaskEither } from "fp-ts/TaskEither";
import {
  useFormatMessage,
  useFormatNonResidentsAdditionalDocumentsLabel,
} from "../../intl";
import { constant, constNull, constVoid, pipe } from "fp-ts/function";
import { NextButton } from "../../Common/NextButton";
import { useCommand, useQuery } from "../../useAPI";

import { Option } from "fp-ts/Option";
import { ScannedDocument } from "../../UploadDocuments/domain";
import { CoApplicantInput } from "../../globalDomain";
import * as uploadApi from "../api";
import { DocumentUploadField } from "./DocumentUploadField";
import { NonResidentsAdditionalDocumentsType } from "../../UKontoSecondPart/domain";
import { Task } from "fp-ts/Task";
import { base64FileSize } from "../PermanentAddress/base64FileSize";
import { useState } from "react";
import * as ukontoApi from "../../UKontoSecondPart/api";
import * as remoteData from "../../RemoteData";
import { RestoreApplicationData } from "../../UKontoSecondPart/api";
import { MultipleDocumentsField } from "./MultipleDocumentsField";

type Error = "SizeExceeding" | "FailureUpload";

type Props = {
  onContinue: TaskEither<unknown, unknown>;
  onFailure: () => unknown;
  disabled: boolean;
  onRemoveLastDoc: () => unknown;
  restoredData?: RestoreApplicationData;
} & CoApplicantInput;

export function AdditionalDocuments(props: Props) {
  const formatMessage = useFormatMessage();
  const formatNonResidentsAdditionalDocumentsLabel = useFormatNonResidentsAdditionalDocumentsLabel();

  const [error, setError] = useState<Option<Error>>(option.none);
  const saveApplication = useCommand(ukontoApi.saveWithParams);
  const uploadDocument = useCommand(uploadApi.additionalDocumentUpload);
  const removeDocument = useCommand(uploadApi.additionalDocumentRemove);
  const removeDocuments = useCommand(uploadApi.additionalDocumentsRemove);
  const [getAdditionalRequiredDocuments, refreshList] = useQuery(
    ukontoApi.getAdditionalRequiredDocuments,
    {
      requiredKeys: option.none,
    }
  );
  const [atLeastOneDocumentUploaded, setAtLeastOneDocumentUploaded] = useState(
    props.restoredData && props.restoredData.hasAdditionalDocuments
      ? props.restoredData.hasAdditionalDocuments
      : false
  );

  const maxMB = 20000000;

  const formatError = (error: Error): LocalizedString => {
    switch (error) {
      case "FailureUpload":
        return formatMessage("Identification.UploadDocuments.failure.message");
      case "SizeExceeding":
        return formatMessage(
          "Identification.UploadDocuments.failure.sizeExceeding"
        );
    }
  };

  const onDocumentUpload = (
    documentKey: NonResidentsAdditionalDocumentsType,
    fileContent: ScannedDocument
  ): Task<unknown> => {
    return pipe(
      fileContent,
      taskEither.fromPredicate(
        scannedDocument => base64FileSize(scannedDocument.base64) < maxMB,
        constant<Error>("SizeExceeding")
      ),
      taskEither.chain(file =>
        pipe(
          uploadDocument({
            documentKey,
            fileContent: file.base64,
            coApplicant: props.coApplicant,
          }),
          taskEither.mapLeft(constant<Error>("FailureUpload"))
        )
      ),
      taskEither.fold(
        m => task.fromIO(() => setError(option.some(m))),
        () =>
          pipe(
            task.fromIO(() => {
              setError(option.none);
              setAtLeastOneDocumentUploaded(true);
              refreshList();
            }),
            task.chain(() =>
              task.fromIO(() => {
                saveApplication({
                  feStep: option.some("NON_RESIDENTS_ADDITIONAL_DOCUMENTS"),
                  status: option.some("IN_PROGRESS"),
                  incomeInfo: option.none,
                  transactionsInfo: option.none,
                })();
                return setError(option.none);
              })
            )
          )
      )
    );
  };

  const onDocumentRemove = (
    documentKey: NonResidentsAdditionalDocumentsType
  ): Task<unknown> => {
    return pipe(
      removeDocument({ documentKey, coApplicant: props.coApplicant }),
      taskEither.fold(
        () => task.fromIO(constVoid),
        response =>
          pipe(
            task.fromIO(() => {
              setError(option.none);
              setAtLeastOneDocumentUploaded(response.atLeastOneDocUploaded);
              if (!response.atLeastOneDocUploaded) {
                props.onRemoveLastDoc();
              }
              refreshList();
            }),
            task.chain(() =>
              task.fromIO(() => {
                saveApplication({
                  feStep: option.some("NON_RESIDENTS_ADDITIONAL_DOCUMENTS"),
                  status: option.some("IN_PROGRESS"),
                  incomeInfo: option.none,
                  transactionsInfo: option.none,
                })();
                return setError(option.none);
              })
            )
          )
      )
    );
  };

  const onRemoveDocuments = (
    documentKeys: Array<NonResidentsAdditionalDocumentsType>
  ): Task<unknown> => {
    return pipe(
      removeDocuments({ documentKeys, coApplicant: props.coApplicant }),
      taskEither.fold(
        () => task.fromIO(constVoid),
        response =>
          pipe(
            task.fromIO(() => {
              setError(option.none);
              setAtLeastOneDocumentUploaded(response.atLeastOneDocUploaded);
              refreshList();
              if (!response.atLeastOneDocUploaded) {
                props.onRemoveLastDoc();
              }
            }),
            task.chain(() =>
              task.fromIO(() => {
                saveApplication({
                  feStep: option.some("NON_RESIDENTS_ADDITIONAL_DOCUMENTS"),
                  status: option.some("IN_PROGRESS"),
                  incomeInfo: option.none,
                  transactionsInfo: option.none,
                })();
                return setError(option.none);
              })
            )
          )
      )
    );
  };

  const onDocumentChange = (
    documentKey: NonResidentsAdditionalDocumentsType
  ) => (fileContent: Option<ScannedDocument>): unknown => {
    return pipe(
      fileContent,
      option.fold(
        () => onDocumentRemove(documentKey),
        file => onDocumentUpload(documentKey, file)
      )
    )();
  };

  const loadingError = () => (
    <ErrorBanner>
      {formatMessage("StandardLoan.UploadDocuments.loadingError")}
    </ErrorBanner>
  );

  const loader = () => (
    <Box hAlignContent="center">
      <Loader />
    </Box>
  );

  const renderDocUploadField = (
    documentKey: NonResidentsAdditionalDocumentsType,
    uploaded: boolean
  ) => (
    <Box column grow shrink>
      <Space units={4} />
      <Box column>
        <DocumentUploadField
          onChange={onDocumentChange(documentKey)}
          value={option.none}
          uploaded={uploaded}
          issues={option.none}
          label={formatNonResidentsAdditionalDocumentsLabel(documentKey)}
          disabled={props.disabled}
          disableMobileUpload
          coApplicant={props.coApplicant}
        />
      </Box>
    </Box>
  );

  const renderMultipleDocUploadField = (
    documentKeys: Array<NonResidentsAdditionalDocumentsType>,
    uploaded: boolean
  ) => (
    <Box column grow shrink>
      <Space units={4} />
      <Box column>
        <MultipleDocumentsField
          requiredDocuments={documentKeys}
          onUploadComplete={pipe(
            taskEither.fromIO(() => refreshList()),
            taskEither.chain(() =>
              taskEither.fromIO(() =>
                saveApplication({
                  feStep: option.some("NON_RESIDENTS_ADDITIONAL_DOCUMENTS"),
                  status: option.some("IN_PROGRESS"),
                  incomeInfo: option.none,
                  transactionsInfo: option.none,
                })()
              )
            )
          )}
          onRemove={onRemoveDocuments(documentKeys)}
          uploaded={uploaded}
          issues={option.none}
          disabled={props.disabled}
          disableMobileUpload
          coApplicant={props.coApplicant}
        />
      </Box>
    </Box>
  );

  const documentList = () => {
    return pipe(
      getAdditionalRequiredDocuments,
      remoteData.fold(loader, loadingError, response => {
        return pipe(
          response.additionalRequiredDocuments,
          option.fold(loadingError, documents => {
            return (
              <Box column grow shrink>
                {pipe(
                  documents,
                  array.map(document =>
                    option.isSome(document.documentKey)
                      ? renderDocUploadField(
                          document.documentKey.value,
                          document.uploaded
                        )
                      : option.isSome(document.documentKeys)
                      ? renderMultipleDocUploadField(
                          document.documentKeys.value,
                          document.uploaded
                        )
                      : loadingError()
                  )
                )}
              </Box>
            );
          })
        );
      })
    );
  };

  const content = pipe(
    <Box column shrink>
      <Box column shrink>
        <Body size="small" weight="regular">
          {formatMessage(
            "Identification.UploadDocuments.step1.otherDocument.description"
          )}
        </Body>
        <Space units={4} />
        {documentList()}
      </Box>
      <Space units={10} />
      <Box hAlignContent="right">
        <NextButton
          action={props.onContinue}
          disabled={
            props.disabled ? props.disabled : !atLeastOneDocumentUploaded
          }
        />
      </Box>
    </Box>
  );

  return (
    <Box grow hAlignContent="center" width="100%">
      {content}
      {pipe(
        error,
        option.fold(constNull, e => <ErrorBanner>{formatError(e)}</ErrorBanner>)
      )}
    </Box>
  );
}
